/* eslint-disable import/order */
import { FC, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { EastOutlined, WestOutlined } from '@mui/icons-material';
import { VlQuoteIcon } from '../icons/icons';

interface Testimonial {
  name?: string;
  location?: string;
  title?: string;
  text?: string;
  image?: string;
  collanColor?: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

export const TestimonialCarousel: FC<TestimonialCarouselProps> = ({ testimonials }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperClass | null>(null);

  const handleSwiperInit = (swiper: SwiperClass) => {
    swiperRef.current = swiper;
    setActiveIndex(swiper.activeIndex);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.activeIndex);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 relative">
      <Swiper
        navigation={{
          nextEl: '.nav-button-next',
          prevEl: '.nav-button-prev',
          disabledClass: 'opacity-50 cursor-not-allowed',
        }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={true}
        modules={[Navigation]}
        spaceBetween={30}
        slidesPerView={1}
        onSwiper={handleSwiperInit}
        onSlideChange={handleSlideChange}
      >
        {testimonials.map((testimonial, index) => (
          <SwiperSlide key={index}>
            <div className="p-9 m-5">
              <div className="px-8 py-10 md:p-10 rounded-[50px] border border-dashed border-black relative ">
                {/* Quote Icon */}
                <div
                  className={`${
                    testimonial.collanColor && testimonial.collanColor
                  } text-9xl absolute -top-14 left-10`}
                >
                  <VlQuoteIcon />
                </div>

                {/* Testimonial Content */}
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex flex-col items-center text-center md:text-left md:items-start max-w-48 w-full flex-shrink-0">
                    {testimonial.image ? (
                      <img
                        src={testimonial.image}
                        alt={testimonial.name || 'User image'}
                        className="w-20 h-20 rounded-2xl object-cover mb-2"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-200 rounded-2xl flex items-center justify-center text-gray-500 text-xl mb-2">
                        ?
                      </div>
                    )}

                    {testimonial.name && (
                      <h3 className="text-2xl leading-7 font-medium text-primary-500 mb-1">
                        {testimonial.name}
                      </h3>
                    )}
                    {testimonial.location && (
                      <p className="font-bold ">
                        {testimonial.location.split(',').map((part, index) => (
                          <span key={index}>
                            {part.trim()}
                            {index === 0 && <br />}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>

                  <div className="text-center md:text-left border-t-2 md:border-t-0 md:border-l-2 border-gray-300 pt-4 md:pt-0 md:pl-8">
                    {testimonial.title && (
                      <h4 className="text-3xl font-medium mb-3">{testimonial.title}</h4>
                    )}
                    {testimonial.text && (
                      <p className="text-xl font-normal leading-loose">{testimonial.text}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation Buttons (Same style for both) */}
      <button
        className={` absolute left-0 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-primary-500 text-white flex items-center justify-center transition-all ${
          activeIndex === 0 ? '' : '' //opacity-50 cursor-not-allowed
        }`}
        aria-label="Previous"
      >
        <WestOutlined />
      </button>

      <button
        className={` absolute right-0 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-primary-500 text-white flex items-center justify-center transition-all ${
          activeIndex === testimonials.length - 1 ? '' : ''
        }`}
        aria-label="Next"
      >
        <EastOutlined />
      </button>
    </div>
  );
};
